(() => {
  const motion = window.PerfumePalaceMotion;
  const reducedMotion = motion?.reducedMotion || window.matchMedia('(prefers-reduced-motion: reduce)');
  const headerScroll = {
    compactAt: 100,
    restoreAt: 16,
    frame: 0,
    header: null,
    listening: false,
    state: null
  };

  const updateHeaderState = () => {
    const header = headerScroll.header;
    if (!header) return;

    const scrollPosition = Math.max(0, window.scrollY);
    let nextState = headerScroll.state;
    if (nextState === null) nextState = scrollPosition >= headerScroll.compactAt ? 'compact' : 'top';
    if (nextState === 'top' && scrollPosition >= headerScroll.compactAt) nextState = 'compact';
    if (nextState === 'compact' && scrollPosition <= headerScroll.restoreAt) nextState = 'top';
    if (nextState === headerScroll.state && header.dataset.ppHeaderState === nextState) return;

    headerScroll.state = nextState;
    header.dataset.ppHeaderState = nextState;
    header.classList.toggle('header--top', nextState === 'top');
    header.classList.toggle('header--compact', nextState === 'compact');
  };

  const queueHeaderUpdate = () => {
    if (headerScroll.frame) return;
    headerScroll.frame = window.requestAnimationFrame(() => {
      headerScroll.frame = 0;
      updateHeaderState();
    });
  };

  const revealGroups = [
    { container: '.pp-home-hero', items: ['.pp-home-hero__content', '.pp-home-hero__media'], step: 120 },
    { container: '[data-section-type="pp-gender-navigation"]', items: '.pp-v2-category-card', step: 80 },
    { container: '[data-section-type="pp-featured-products"]', items: '.pp-v2-product-card', step: 80 },
    { container: '[data-section-type="pp-featured-fragrances"]', items: '.pp-v2-product-card', step: 80 },
    { container: '[data-section-type="pp-scent-family"]', items: '.pp-v2-scent-circle', step: 80 },
    { container: '[data-section-type="pp-faq"]', items: '.pp-accordion', step: 70 },
    { container: '[data-section-type="pp-footer"]', items: '.pp-v2-footer__newsletter, .pp-footer__brand, .pp-footer__group, .pp-v2-footer__verified-info, .pp-footer__bottom', step: 80 },
    { container: '[data-pp-product-page]', items: '.pp-product-gallery, .pp-product-page__decision-column, .pp-product-page__post-purchase', step: 90 },
    { container: '.pp-related-products', items: '.pp-v2-product-card', step: 80 }
  ];

  const matchingElements = (scope, selector) => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector)
  ];

  const prepareRevealTargets = (scope = document) => {
    revealGroups.forEach((group) => {
      matchingElements(scope, group.container).forEach((container) => {
        const items = Array.isArray(group.items)
          ? group.items.flatMap((selector) => Array.from(container.querySelectorAll(selector)))
          : Array.from(container.querySelectorAll(group.items));
        items.forEach((item, index) => {
          item.dataset.ppReveal = '';
          item.style.setProperty('--pp-reveal-delay', `${Math.min(index * group.step, 360)}ms`);
        });
      });
    });
  };

  const initializeAccordionMotion = (scope = document) => {
    const detailsItems = matchingElements(scope, '.pp-faq .pp-accordion, .pp-product-page__accordions details');
    detailsItems.forEach((details) => {
      if (details.dataset.ppAccordionMotionReady === 'true') return;
      details.dataset.ppAccordionMotionReady = 'true';
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;
      let animation = null;

      summary.addEventListener('click', (event) => {
        if (reducedMotion.matches || typeof details.animate !== 'function') return;
        event.preventDefault();
        if (animation) animation.finish();

        const opening = !details.open;
        const startHeight = `${details.offsetHeight}px`;
        if (opening) details.open = true;
        const endHeight = opening ? `${details.offsetHeight}px` : `${summary.offsetHeight}px`;
        details.style.overflow = 'hidden';

        animation = details.animate(
          { height: [startHeight, endHeight], opacity: opening ? [0.72, 1] : [1, 0.72] },
          {
            duration: (motion?.tokens.sectionDuration ?? 320) + (motion?.tokens.fastDuration ?? 160),
            easing: window.getComputedStyle(document.documentElement).getPropertyValue('--pp-ease-enter').trim()
          }
        );
        animation.onfinish = () => {
          if (!opening) details.open = false;
          details.style.removeProperty('height');
          details.style.removeProperty('overflow');
          details.style.removeProperty('opacity');
          animation = null;
        };
      });
    });
  };

  const initializeReveal = (scope = document) => {
    prepareRevealTargets(scope);
    if (document.documentElement.classList.contains('pp-preloader-active')) return;
    const items = Array.from(scope.querySelectorAll('[data-pp-reveal]:not([data-pp-reveal-ready])'));
    if (items.length === 0) return;

    const pendingItems = [];
    items.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        item.classList.add('is-visible');
      } else {
        pendingItems.push(item);
      }
      item.dataset.ppRevealReady = 'true';
    });

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    pendingItems.forEach((item) => {
      observer.observe(item);
      window.setTimeout(() => {
        if (item.classList.contains('is-visible')) return;
        item.classList.add('is-visible');
        observer.unobserve(item);
      }, 1800);
    });
  };

  const initializeHeader = () => {
    const header = document.querySelector('[data-pp-header-shell]');
    if (!header) return;

    if (headerScroll.header !== header) {
      headerScroll.header = header;
      headerScroll.state = null;
      updateHeaderState();
    }

    if (!headerScroll.listening) {
      headerScroll.listening = true;
      window.addEventListener('scroll', queueHeaderUpdate, { passive: true });
      document.addEventListener('click', (event) => {
        const activeHeader = headerScroll.header;
        if (!activeHeader || activeHeader.contains(event.target)) return;
        activeHeader.querySelectorAll('[data-pp-menu-disclosure][open]').forEach((details) => {
          details.removeAttribute('open');
        });
      });
    }

    if (header.dataset.ppHeaderReady === 'true') return;
    header.dataset.ppHeaderReady = 'true';

    const disclosures = Array.from(header.querySelectorAll('[data-pp-menu-disclosure]'));
    disclosures.forEach((details) => {
      let hoverTimer;
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        disclosures.forEach((other) => { if (other !== details) other.removeAttribute('open'); });
      });
      details.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !details.open) return;
        event.preventDefault();
        details.removeAttribute('open');
        details.querySelector('summary')?.focus();
      });
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        details.addEventListener('mouseenter', () => {
          window.clearTimeout(hoverTimer);
          details.setAttribute('open', '');
        });
        details.addEventListener('mouseleave', () => {
          hoverTimer = window.setTimeout(() => {
            if (!details.contains(document.activeElement)) details.removeAttribute('open');
          }, 140);
        });
        details.addEventListener('focusout', () => {
          window.requestAnimationFrame(() => {
            if (!details.contains(document.activeElement)) details.removeAttribute('open');
          });
        });
      }
    });

  };

  const initializeDialogs = () => {
    const openers = new WeakMap();
    const choiceDefaults = new WeakMap();
    const syncDialogLock = () => {
      document.body.classList.toggle('pp-header-dialog-open', Boolean(document.querySelector('.pp-header__dialog[open]')));
    };
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-pp-open]');
      if (!opener) return;
      if (opener.hasAttribute('data-pp-card-intent') && opener.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }
      const dialog = document.querySelector(opener.getAttribute('data-pp-open'));
      if (!dialog) return;
      document.querySelectorAll('.pp-header__dialog[open]').forEach((other) => {
        if (other !== dialog) other.close();
      });
      openers.set(dialog, opener);
      dialog.ppReturnFocus = opener;
      if (dialog.hasAttribute('data-pp-card-choice')) {
        const form = dialog.querySelector('[data-pp-product-form]');
        if (form?.dataset.ppCartPending === 'true') return;
        const intent = opener.dataset.ppCardIntent;
        dialog.dataset.ppPurchaseIntent = intent || '';
        dialog.querySelector('[data-pp-add-to-cart]')?.toggleAttribute('hidden', intent === 'buy');
        dialog.querySelector('[data-pp-buy-now]')?.toggleAttribute('hidden', intent === 'add');
        dialog.ppCard = opener.closest('[data-pp-product-card]');
        const select = dialog.querySelector('[data-pp-explicit-choice]');
        if (select) {
          if (!choiceDefaults.has(dialog)) {
            choiceDefaults.set(dialog, [...dialog.querySelectorAll('[data-pp-variant-price], [data-pp-variant-compare], [data-pp-quick-view-image], [data-pp-offer-variants]')].map((element) => ({ current: element, initial: element.cloneNode(true) })));
          }
          choiceDefaults.get(dialog).forEach((entry) => {
            const restored = entry.initial.cloneNode(true);
            entry.current.replaceWith(restored);
            entry.current = restored;
          });
          select.value = '';
          dialog.querySelectorAll('[data-pp-add-to-cart], [data-pp-buy-now]').forEach(button => { button.disabled = true; });
        }
        const status = dialog.querySelector('[data-pp-product-form-status]');
        if (status) { status.hidden = true; status.textContent = ''; }
      }
      if (dialog.dataset.ppReturnReady !== 'true') {
        dialog.dataset.ppReturnReady = 'true';
        dialog.addEventListener('close', () => {
          syncDialogLock();
          openers.get(dialog)?.focus({ preventScroll: true });
        });
      }
      window.requestAnimationFrame(() => {
        syncDialogLock();
        const initialFocus = dialog.querySelector('[data-pp-explicit-choice], [data-pp-predictive-input], [autofocus]') || dialog.querySelector('button, a, input, select');
        initialFocus?.focus({ preventScroll: true });
      });
    });
  };

  const initializeProductCards = (scope = document) => {
    scope.querySelectorAll('[data-pp-variant-select]:not([data-pp-variant-ready])').forEach((select) => {
      select.dataset.ppVariantReady = 'true';
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const productScope = select.closest('[data-pp-product-card], dialog');
        if (!option || !productScope) return;

        const available = option.dataset.available === 'true';
        const price = productScope.querySelector('[data-pp-variant-price]');
        const compare = productScope.querySelector('[data-pp-variant-compare]');
        const button = select.closest('form')?.querySelector('[data-pp-add-to-cart]');
        window.PerfumePalacePrice?.set(price, option.dataset.priceCents, option.dataset.price || '');
        if (compare) {
          window.PerfumePalacePrice?.set(compare, option.dataset.comparePriceCents, option.dataset.comparePrice || '');
          compare.hidden = !option.dataset.comparePrice;
        }
        if (button) {
          button.disabled = !available;
          const label = available ? button.dataset.availableLabel : button.dataset.soldOutLabel;
          const labelNode = button.querySelector('[data-pp-button-label]');
          if (labelNode) labelNode.textContent = label;
          else button.textContent = label;
        }
        const image = productScope.querySelector('[data-pp-quick-view-image], .pp-v2-product-card__image--primary');
        if (image && option.dataset.imageSrc) {
          image.src = option.dataset.imageSrc;
          image.removeAttribute('srcset');
          if (option.dataset.imageAlt) image.alt = option.dataset.imageAlt;
        }
      });
    });
  };

  const initializeFeaturedMerchandising = (scope = document) => {
    scope.querySelectorAll('[data-pp-featured-merchandising]:not([data-pp-merchandising-ready])').forEach((root) => {
      root.dataset.ppMerchandisingReady = 'true';
      const url = root.dataset.ppRecommendationsUrl;
      const support = root.querySelector('[data-pp-featured-support]');
      if (!url || !support) return;

      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const source = documentFragment.querySelector('[data-pp-featured-recommendation-source]');
          if (!source || source.querySelectorAll('[data-pp-product-card]').length === 0) return;
          support.replaceChildren(...Array.from(source.children));
          support.dataset.recommendationSource = 'shopify-related';
          initializeReveal(support);
          initializeProductCards(support);
        })
        .catch(() => {
          support.dataset.recommendationSource = 'collection-fallback';
        });
    });
  };

  const initializeScentCarousel = (root) => {
    if (root.dataset.ppCarouselReady === 'true') return;
    root.dataset.ppCarouselReady = 'true';
    const track = root.querySelector('[data-pp-carousel-track]');
    const previous = root.querySelector('[data-pp-carousel-previous]');
    const next = root.querySelector('[data-pp-carousel-next]');
    const items = Array.from(track?.children || []);
    if (!track || items.length < 2) return;

    const mobile = window.matchMedia('(max-width: 749px)');
    const hoverCapable = window.matchMedia('(hover: hover)');
    const pauseReasons = new Set();
    let index = 0;
    let direction = 1;
    let timer = 0;
    let frame = 0;
    let scrollTimer = 0;
    let wheelTimer = 0;
    let manualTimer = 0;
    let drag = null;
    let suppressClick = false;

    const itemDistance = (itemIndex) => Math.abs(items[itemIndex].offsetLeft - items[0].offsetLeft);
    const targetOffset = (itemIndex) => (
      (motion?.directionSign(track) ?? (window.getComputedStyle(track).direction === 'rtl' ? -1 : 1))
      * itemDistance(itemIndex)
    );
    const autoplayDelay = () => (
      mobile.matches
        ? (motion?.tokens.autoAdvanceDelay ?? 4200)
        : Number(root.dataset.interval || 6000)
    );
    const carouselDuration = () => motion?.tokens.carouselDuration ?? 320;
    const canPlay = () => (
      root.dataset.autoplay === 'true'
      && !reducedMotion.matches
      && !document.hidden
      && pauseReasons.size === 0
      && !root.contains(document.activeElement)
    );

    const clearTimer = () => {
      (motion?.timers.clear || window.clearTimeout)(timer);
      timer = 0;
    };

    const cancelAnimation = () => {
      (motion?.frames.cancel || window.cancelAnimationFrame)(frame);
      frame = 0;
    };

    const syncIndex = () => {
      const position = Math.abs(track.scrollLeft);
      index = items.reduce((nearest, item, itemIndex) => (
        Math.abs(position - itemDistance(itemIndex)) < Math.abs(position - itemDistance(nearest))
          ? itemIndex
          : nearest
      ), 0);
      if (index === 0) direction = 1;
      if (index === items.length - 1) direction = -1;
    };

    const schedule = (delay = autoplayDelay()) => {
      clearTimer();
      if (!canPlay()) return;
      timer = (motion?.timers.set || window.setTimeout)(advance, delay);
    };

    const animateTo = (nextIndex, reschedule = true) => {
      cancelAnimation();
      const boundedIndex = Math.max(0, Math.min(items.length - 1, nextIndex));
      const start = track.scrollLeft;
      const target = targetOffset(boundedIndex);
      const duration = carouselDuration();

      if (reducedMotion.matches || duration === 0) {
        track.scrollLeft = target;
        index = boundedIndex;
        if (reschedule) schedule();
        return;
      }

      const startedAt = motion?.now() ?? window.performance.now();
      const step = (now) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = motion?.easing.outCubic(progress) ?? (1 - Math.pow(1 - progress, 3));
        track.scrollLeft = start + ((target - start) * eased);
        if (progress < 1) {
          frame = (motion?.frames.request || window.requestAnimationFrame)(step);
          return;
        }
        frame = 0;
        index = boundedIndex;
        if (reschedule) schedule();
      };

      frame = (motion?.frames.request || window.requestAnimationFrame)(step);
    };

    function advance() {
      if (!canPlay()) return;
      if (index >= items.length - 1) direction = -1;
      if (index <= 0) direction = 1;
      animateTo(index + direction);
    }

    const pause = (reason) => {
      pauseReasons.add(reason);
      clearTimer();
    };

    const resume = (reason, delay) => {
      pauseReasons.delete(reason);
      syncIndex();
      schedule(delay);
    };

    const manualMove = (nextIndex) => {
      pause('manual');
      (motion?.timers.clear || window.clearTimeout)(manualTimer);
      animateTo(nextIndex, false);
      manualTimer = (motion?.timers.set || window.setTimeout)(
        () => resume('manual'),
        carouselDuration() + (motion?.tokens.standardDuration ?? 220)
      );
    };

    const moveByVisualDirection = (directionStep) => {
      syncIndex();
      manualMove(Math.max(0, Math.min(items.length - 1, index + directionStep)));
    };

    previous?.addEventListener('click', () => moveByVisualDirection(-1));
    next?.addEventListener('click', () => moveByVisualDirection(1));
    root.addEventListener('mouseenter', () => { if (hoverCapable.matches) pause('hover'); });
    root.addEventListener('mouseleave', () => { if (hoverCapable.matches) resume('hover'); });
    root.addEventListener('focusin', () => pause('focus'));
    root.addEventListener('focusout', (event) => {
      if (root.contains(event.relatedTarget)) return;
      pauseReasons.delete('focus');
      syncIndex();
      (motion?.frames.request || window.requestAnimationFrame)(() => schedule());
    });
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      drag = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startScroll: track.scrollLeft,
        dragging: false,
        moved: false
      };
      pause('pointer');
      cancelAnimation();
    });
    track.addEventListener('pointermove', (event) => {
      if (!drag || drag.id !== event.pointerId) return;
      const delta = event.clientX - drag.startX;
      const verticalDelta = event.clientY - drag.startY;
      if (!drag.dragging) {
        if (Math.abs(verticalDelta) > Math.abs(delta)) {
          resume('pointer');
          drag = null;
          return;
        }
        if (Math.abs(delta) <= 6) return;
        drag.dragging = true;
        drag.moved = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      track.scrollLeft = drag.startScroll - delta;
    });
    const finishDrag = (event) => {
      if (!drag || drag.id !== event.pointerId) return;
      track.classList.remove('is-dragging');
      suppressClick = drag.moved;
      drag = null;
      resume('pointer');
    };
    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', finishDrag);
    track.addEventListener('click', (event) => {
      if (suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }
    }, true);
    track.addEventListener('keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const rtl = window.getComputedStyle(track).direction === 'rtl';
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const visualStep = event.key === 'ArrowRight' ? 1 : -1;
        moveByVisualDirection(rtl ? -visualStep : visualStep);
      } else if (event.key === 'Home') {
        event.preventDefault();
        manualMove(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        manualMove(items.length - 1);
      }
    });
    track.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) return;
      pause('wheel');
      (motion?.timers.clear || window.clearTimeout)(wheelTimer);
      wheelTimer = (motion?.timers.set || window.setTimeout)(() => resume('wheel'), 800);
    }, { passive: true });
    track.addEventListener('scroll', () => {
      (motion?.timers.clear || window.clearTimeout)(scrollTimer);
      scrollTimer = (motion?.timers.set || window.setTimeout)(syncIndex, 120);
    }, { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        clearTimer();
        cancelAnimation();
        return;
      }
      schedule();
    });
    reducedMotion.addEventListener?.('change', () => {
      clearTimer();
      cancelAnimation();
      schedule();
    });
    mobile.addEventListener?.('change', () => {
      syncIndex();
      schedule();
    });
    syncIndex();
    schedule();
  };

  const initializeHeroCarousel = (root) => {
    if (root.dataset.ppHeroReady === 'true') return;
    const slides = Array.from(root.querySelectorAll('[data-pp-hero-slide]'));
    if (!slides.length) return;
    root.dataset.ppHeroReady = 'true';
    const status = root.querySelector('[data-pp-hero-status]');
    const stage = root.querySelector('[data-pp-hero-stage]');
    const events = new AbortController();
    const on = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: events.signal });
    const pauseReasons = new Set(['offscreen']);
    const mobileViewport = window.matchMedia('(max-width: 989px)');
    const autoplayEnabled = () => root.dataset.autoplay === 'true' || (mobileViewport.matches && root.dataset.autoplayMobile === 'true');
    const interval = Math.max(5000, Number(root.dataset.interval || 7000));
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer = 0;
    let typingFrame = 0;
    let pointerStart = null;
    const clearTimer = () => { window.clearTimeout(timer); timer = 0; };
    const stopTyping = () => {
      cancelAnimationFrame(typingFrame);
      typingFrame = 0;
      slides.forEach((slide) => slide.querySelector('[data-pp-hero-typing]')?.classList.remove('is-typing'));
    };
    const typeHeading = (allow = true) => {
      const heading = slides[index].querySelector('[data-pp-hero-typing]');
      if (!heading) return;
      if (!allow || reducedMotion.matches || root.dataset.typing !== 'true') {
        stopTyping();
        heading.dataset.ppTyped = 'true';
        return;
      }
      if (heading.dataset.ppTyped === 'true' || document.hidden || pauseReasons.has('offscreen')) return;
      stopTyping();
      heading.dataset.ppTyped = 'true';
      typingFrame = requestAnimationFrame(() => { typingFrame = 0; heading.classList.add('is-typing'); });
    };
    const schedule = () => {
      clearTimer();
      if (!autoplayEnabled() || slides.length < 2 || reducedMotion.matches || document.hidden || pauseReasons.size) return;
      timer = window.setTimeout(() => show(index + 1), interval);
    };
    const hydrate = (slide) => {
      slide.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      });
      slide.querySelectorAll('img[data-src]').forEach((image) => {
        image.loading = 'eager';
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        image.removeAttribute('data-srcset');
        image.removeAttribute('data-src');
      });
    };
    const show = (nextIndex, announce = false, allowTyping = true) => {
      stopTyping();
      const oldSlide = slides[index];
      const moveFocus = oldSlide?.contains(document.activeElement);
      index = (nextIndex + slides.length) % slides.length;
      hydrate(slides[index]);
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      if (moveFocus && oldSlide !== slides[index]) {
        const target = slides[index].querySelector('a[href]') || root.querySelector('[data-pp-hero-stage]');
        target?.focus({ preventScroll: true });
      }
      if (status && announce) status.textContent = `${index + 1} / ${slides.length}`;
      typeHeading(allowTyping);
      schedule();
    };
    const pause = (reason) => { pauseReasons.add(reason); clearTimer(); if (['offscreen', 'focus', 'editor'].includes(reason)) stopTyping(); };
    const resume = (reason) => { pauseReasons.delete(reason); typeHeading(); schedule(); };
    const navigate = (target, keyboard = false) => { clearTimer(); show(target, true, !keyboard); };
    const rtlStep = (step) => window.getComputedStyle(root).direction === 'rtl' ? -step : step;
    on(root, 'pointerenter', (event) => { if (event.pointerType === 'mouse') pause('hover'); });
    on(root, 'pointerleave', (event) => { if (event.pointerType === 'mouse') resume('hover'); });
    on(root, 'focusin', () => pause('focus'));
    on(root, 'focusout', (event) => { if (!root.contains(event.relatedTarget)) resume('focus'); });
    on(root, 'keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.target.matches('input, textarea, select, [contenteditable="true"]')) return;
      let target;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') target = index + rtlStep(event.key === 'ArrowRight' ? 1 : -1);
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = slides.length - 1;
      else return;
      event.preventDefault();
      pause('focus');
      navigate(target, true);
    });
    on(root, 'pointerdown', (event) => {
      if (event.target.closest('a, button')) return;
      if (event.pointerType === 'mouse' && (event.button !== 0 || !event.target.closest('.pp-banner-hero__media'))) return;
      pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      root.setPointerCapture(event.pointerId);
      if (event.pointerType === 'mouse') root.classList.add('is-dragging');
      pause('touch');
    }, { passive: true });
    on(root, 'pointerup', (event) => {
      if (!pointerStart || pointerStart.id !== event.pointerId) return;
      const dx = event.clientX - pointerStart.x;
      const dy = event.clientY - pointerStart.y;
      pointerStart = null;
      root.classList.remove('is-dragging');
      if (root.hasPointerCapture(event.pointerId)) root.releasePointerCapture(event.pointerId);
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.3) navigate(index + rtlStep(dx < 0 ? 1 : -1));
      if (document.activeElement === stage) stage.blur();
      if (!root.contains(document.activeElement)) pauseReasons.delete('focus');
      resume('touch');
    }, { passive: true });
    on(root, 'pointercancel', (event) => { pointerStart = null; root.classList.remove('is-dragging'); if (root.hasPointerCapture(event.pointerId)) root.releasePointerCapture(event.pointerId); if (document.activeElement === stage) stage.blur(); if (!root.contains(document.activeElement)) pauseReasons.delete('focus'); resume('touch'); }, { passive: true });
    on(root, 'dragstart', (event) => { if (event.target.closest('.pp-banner-hero__media')) event.preventDefault(); });
    on(document, 'visibilitychange', () => { if (document.hidden) stopTyping(); else typeHeading(); schedule(); });
    on(reducedMotion, 'change', () => { stopTyping(); typeHeading(); schedule(); });
    on(mobileViewport, 'change', schedule);
    on(root, 'animationend', (event) => { if (event.target.matches('[data-pp-hero-typing]')) event.target.classList.remove('is-typing'); });
    on(root, 'shopify:block:select', (event) => {
      const selected = event.target.closest?.('[data-pp-hero-slide]');
      const selectedIndex = slides.findIndex((slide) => slide === selected || slide.dataset.blockId === event.detail?.blockId);
      if (selectedIndex < 0) return;
      pause('editor');
      show(selectedIndex, true, false);
    });
    on(root, 'shopify:block:deselect', () => resume('editor'));
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) resume('offscreen');
      else pause('offscreen');
    }, { threshold: 0.1 });
    observer.observe(root);
    on(document, 'shopify:section:unload', (event) => {
      if (event.target !== root && !event.target.contains(root)) return;
      clearTimer();
      observer.disconnect();
      stopTyping();
      events.abort();
      delete root.dataset.ppHeroReady;
    });
    show(index);
  };


  const initializeStoryCarousel = (root) => {
    if (root.dataset.ppStoryReady === 'true') return;
    root.dataset.ppStoryReady = 'true';
    const slides = Array.from(root.querySelectorAll('[data-pp-story-slide]'));
    const dots = Array.from(root.querySelectorAll('.pp-brand-story__dots span'));
    const previous = root.querySelector('[data-pp-story-previous]');
    const next = root.querySelector('[data-pp-story-next]');
    if (slides.length < 2) return;

    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let pointerStart = null;
    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
    };
    const directionStep = (step) => window.getComputedStyle(root).direction === 'rtl' ? -step : step;
    previous?.addEventListener('click', () => show(index + directionStep(-1)));
    next?.addEventListener('click', () => show(index + directionStep(1)));
    root.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      show(index + directionStep(event.key === 'ArrowRight' ? 1 : -1));
    });
    root.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      pointerStart = event.clientX;
    }, { passive: true });
    root.addEventListener('pointerup', (event) => {
      if (pointerStart === null) return;
      const delta = event.clientX - pointerStart;
      pointerStart = null;
      if (Math.abs(delta) < 45) return;
      show(index + directionStep(delta < 0 ? 1 : -1));
    }, { passive: true });
    root.addEventListener('shopify:block:select', (event) => {
      const selected = event.target.closest?.('[data-pp-story-slide]');
      const selectedIndex = slides.indexOf(selected);
      if (selectedIndex >= 0) show(selectedIndex);
    });
    show(index);
  };

  const initializeShelfCarousel = (root) => {
    if (root.dataset.ppShelfReady === 'true') return;
    root.dataset.ppShelfReady = 'true';
    const track = root.querySelector('[data-pp-shelf-track]');
    const items = Array.from(track?.querySelectorAll('.pp-v2-product-card') || []);
    const previous = root.querySelector('[data-pp-shelf-previous]');
    const next = root.querySelector('[data-pp-shelf-next]');
    if (!track || items.length < 2) return;

    const closestIndex = () => {
      const trackRect = track.getBoundingClientRect();
      const edge = window.getComputedStyle(track).direction === 'rtl' ? trackRect.right : trackRect.left;
      return items.reduce((best, item, itemIndex) => {
        const rect = item.getBoundingClientRect();
        const itemEdge = window.getComputedStyle(track).direction === 'rtl' ? rect.right : rect.left;
        const distance = Math.abs(itemEdge - edge);
        return distance < best.distance ? { index: itemIndex, distance } : best;
      }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
    };
    const moveTo = (target) => {
      items[target].scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    };
    const move = (step) => {
      const target = Math.max(0, Math.min(items.length - 1, closestIndex() + step));
      moveTo(target);
    };
    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    track.addEventListener('keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const rtl = window.getComputedStyle(track).direction === 'rtl';
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const visualStep = event.key === 'ArrowRight' ? 1 : -1;
        move(rtl ? -visualStep : visualStep);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        moveTo(event.key === 'Home' ? 0 : items.length - 1);
      }
    });
  };

  const initializeMobileFooter = (scope = document) => {
    const mobile = window.matchMedia('(max-width: 749px)');

    matchingElements(scope, '.pp-v2-footer').forEach((footer) => {
      const syncGroups = () => {
        footer.querySelectorAll('.pp-footer__group').forEach((group) => {
          if (mobile.matches) {
            if (group.dataset.ppMobileFooterManaged === 'true') return;
            group.removeAttribute('open');
            group.dataset.ppMobileFooterManaged = 'true';
            return;
          }

          group.setAttribute('open', '');
          delete group.dataset.ppMobileFooterManaged;
        });
      };

      syncGroups();
      if (footer.dataset.ppMobileFooterReady === 'true') return;
      footer.dataset.ppMobileFooterReady = 'true';
      mobile.addEventListener?.('change', syncGroups);
    });
  };

  const initializeAll = (scope = document) => {
    initializeReveal(scope);
    initializeAccordionMotion(scope);
    initializeProductCards(scope);
    initializeFeaturedMerchandising(scope);
    matchingElements(scope, '[data-pp-hero]').forEach(initializeHeroCarousel);
    matchingElements(scope, '[data-pp-story-carousel]').forEach(initializeStoryCarousel);
    matchingElements(scope, '[data-pp-shelf-carousel]').forEach(initializeShelfCarousel);
    scope.querySelectorAll('[data-pp-scent-carousel]').forEach(initializeScentCarousel);
    initializeMobileFooter(scope);
    initializeHeader();
  };

  document.addEventListener('DOMContentLoaded', () => {
    initializeDialogs();
    initializeAll();
  });
  document.addEventListener('pp:reveal:refresh', (event) => initializeReveal(event.detail?.scope || document));
  document.addEventListener('pp:products:loaded', (event) => {
    initializeReveal(event.detail.scope);
    initializeProductCards(event.detail.scope);
  });
  document.addEventListener('pp:preloader:complete', () => initializeReveal());
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
